import React, { useEffect, useState } from 'react';
import PromptService from '../services/PromptService';
import showToast from '../../../../Shared/Utils/ToastNotification';
import '../styles/PromptManagement.css';

// Description mapping for frontend display
const PROMPT_DESCRIPTIONS = {
  clinical_interpreter: 'Analyzes parsed patient lab results (semen and hormones) to generate a detailed clinical interpretation.',
  safety_check_1: 'Evaluates generated patient reports against clinical safety guidelines and legal compliance.',
  safety_check_2: 'Reviews consumer-facing report copy to identify potential contradictions or unsafe language.',
  copy_personalizer:
    'Personalizes the grid copy (home top, results summary, results full, action plan) with the intake answers. Text that fails the backend checks falls back to the grid copy.',
};

const PROMPT_DISPLAY_NAMES = {
  clinical_interpreter: 'Clinical Results Interpreter',
  safety_check_1: 'Safety Check 1 (Clinical & Legal)',
  safety_check_2: 'Safety Check 2 (Consumer)',
  copy_personalizer: 'Copy Personalizer (AI)',
};

// Determines if a section is static (editable) or contains dynamic runtime logic (read-only)
const isSectionEditable = (title) => {
  const t = title.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (t.includes('INPUT') || t.includes('OUTPUT') || t.includes('RECEIVE') || t.includes('SCHEMA')) {
    return false;
  }
  return true;
};

// Parses full prompt content into sections separated by '## ' headings
const parsePrompt = (fullContent) => {
  if (!fullContent) return [];
  const lines = fullContent.split('\n');
  const sections = [];
  let currentSection = { id: 0, title: 'Header', lines: [] };
  let secId = 0;

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('## ')) {
      if (currentSection.lines.length > 0 || currentSection.title !== 'Header') {
        sections.push({
          id: secId++,
          title: currentSection.title,
          content: currentSection.lines.join('\n'),
          isEditable: isSectionEditable(currentSection.title),
        });
      }
      currentSection = { id: secId, title: trimmed, lines: [line] };
    } else {
      currentSection.lines.push(line);
    }
  }

  if (currentSection.lines.length > 0 || currentSection.title !== 'Header') {
    sections.push({
      id: secId++,
      title: currentSection.title,
      content: currentSection.lines.join('\n'),
      isEditable: isSectionEditable(currentSection.title),
    });
  }
  return sections;
};

const PromptManagement = () => {
  const [prompts, setPrompts] = useState([]);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [viewingPrompt, setViewingPrompt] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [saving, setSaving] = useState(false);

  // Section toggle state inside Edit Modal
  const [expandedSections, setExpandedSections] = useState(new Set());

  useEffect(() => {
    fetchPrompts();
  }, []);

  useEffect(() => {
    filterPrompts();
  }, [searchTerm, prompts]);

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const res = await PromptService.getSystemPrompts();
      const fetchedPrompts = res.data.results || res.data || [];
      setPrompts(fetchedPrompts);
    } catch (err) {
      console.error(err);
      showToast('Failed to load prompts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterPrompts = () => {
    if (!searchTerm.trim()) {
      setFilteredPrompts(prompts);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = prompts.filter((prompt) => {
      const displayName = PROMPT_DISPLAY_NAMES[prompt.prompt_type] || prompt.prompt_type;
      const description = PROMPT_DESCRIPTIONS[prompt.prompt_type] || '';
      return displayName.toLowerCase().includes(term) || description.toLowerCase().includes(term) || prompt.prompt_type.toLowerCase().includes(term);
    });
    setFilteredPrompts(filtered);
  };

  const handleCopyClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('Prompt copied to clipboard!', 'success');
  };

  const handleOpenEdit = (prompt) => {
    const parsedSections = parsePrompt(prompt.content);
    setEditingPrompt({
      ...prompt,
      sections: parsedSections,
    });
    // Expand editable sections by default, leave read-only collapsed
    const editableIds = parsedSections.filter((s) => s.isEditable).map((s) => s.id);
    setExpandedSections(new Set(editableIds));
  };

  const handleSectionContentChange = (sectionId, newContent) => {
    setEditingPrompt((prev) => ({
      ...prev,
      sections: prev.sections.map((sec) => (sec.id === sectionId ? { ...sec, content: newContent } : sec)),
    }));
  };

  const toggleSection = (id) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editingPrompt) return;

    // Reconstruct full content from parsed sections, ensuring exact format is preserved
    const fullContent = editingPrompt.sections.map((sec) => sec.content).join('\n');

    setSaving(true);
    try {
      await PromptService.updateSystemPrompt(editingPrompt.id, {
        content: fullContent,
      });
      showToast('Prompt updated successfully', 'success');
      setEditingPrompt(null);
      fetchPrompts();
    } catch (err) {
      console.error(err);
      showToast('Failed to save prompt', 'error');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="prompt-management-container d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-2" role="status"></div>
          <div>Loading system prompts...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="prompt-management-container">
      {/* Title & Refresh section */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div className="prompt-title-section">
          <h2>AI Prompt Management</h2>
          <p className="mb-0">Review and modify system prompts used by the AI engine.</p>
        </div>
        <button className="refresh-btn d-flex align-items-center gap-2" onClick={fetchPrompts}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
          </svg>
          Refresh Table
        </button>
      </div>

      {/* Warning Box */}
      {/* <div className="prompt-warning-alert mb-4">
        <span className="prompt-warning-icon">⚠️</span>
        <div>
          <div className="prompt-warning-title">Important: Keep Dynamic Placeholders Intact</div>
          <div className="prompt-warning-text">
            These prompts contain bracketed variables (like <span className="prompt-highlight-pink">{'{classification}'}</span>,{' '}
            <span className="prompt-highlight-pink">{'{parsed_data}'}</span>, etc.) that the backend uses to inject patient data. When editing, you
            can tweak the wording and guidelines, but <span className="fw-bold text-dark">do not modify or delete</span> these variables as it will
            cause AI generation to fail.
          </div>
        </div>
      </div> */}

      {/* Search group */}
      <div className="mb-4">
        <div className="prompt-search-wrapper">
          <span className="prompt-search-icon">🔍</span>
          <input
            type="text"
            className="prompt-search-input"
            placeholder="Search prompts by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Prompt table wrapper */}
      <div className="prompt-table-container">
        <table className="prompt-custom-table">
          <thead>
            <tr>
              <th style={{ width: '25%' }}>Prompt Name</th>
              <th style={{ width: '40%' }}>Description</th>
              <th style={{ width: '10%' }}>Status</th>
              <th style={{ width: '15%' }}>Last Updated</th>
              <th style={{ width: '10%', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPrompts.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-4 text-muted">
                  No matching prompts found.
                </td>
              </tr>
            ) : (
              filteredPrompts.map((prompt) => {
                const displayName = PROMPT_DISPLAY_NAMES[prompt.prompt_type] || prompt.prompt_type.replace(/_/g, ' ').toUpperCase();
                const description = PROMPT_DESCRIPTIONS[prompt.prompt_type] || 'Standard AI configuration prompt.';

                return (
                  <tr key={prompt.id}>
                    <td className="prompt-name-cell">{displayName}</td>
                    <td className="prompt-desc-cell">{description}</td>
                    <td>
                      <span className={`prompt-status-badge ${prompt.is_active ? '' : 'inactive'}`}>{prompt.is_active ? 'ACTIVE' : 'INACTIVE'}</span>
                    </td>
                    <td className="text-white">{formatDate(prompt.updated_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="d-flex justify-content-end gap-2">
                        <button type="button" className="prompt-btn-view" onClick={() => setViewingPrompt(prompt)}>
                          View
                        </button>
                        <button type="button" className="prompt-btn-edit" onClick={() => handleOpenEdit(prompt)}>
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ================= VIEW MODAL ================= */}
      {viewingPrompt && (
        <div className="prompt-modal-overlay" onClick={() => setViewingPrompt(null)}>
          <div className="prompt-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="prompt-modal-header">
              <div className="d-flex align-items-center gap-2">
                <h5 className="prompt-modal-title">{PROMPT_DISPLAY_NAMES[viewingPrompt.prompt_type] || viewingPrompt.prompt_type}</h5>
                <span className={`prompt-status-badge ${viewingPrompt.is_active ? '' : 'inactive'}`}>
                  {viewingPrompt.is_active ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>
              <button type="button" className="btn-close-white-custom" onClick={() => setViewingPrompt(null)}>
                ×
              </button>
            </div>

            <div className="prompt-modal-body">
              <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                {PROMPT_DESCRIPTIONS[viewingPrompt.prompt_type] || 'Standard AI configuration prompt.'}
              </p>
              <div className="prompt-content-view">{viewingPrompt.content}</div>
            </div>

            <div className="prompt-modal-footer">
              <button
                type="button"
                className="prompt-btn-view"
                onClick={() => handleCopyClipboard(viewingPrompt.content)}
                style={{ padding: '0.5rem 1.25rem' }}
              >
                Copy to Clipboard
              </button>
              <button
                type="button"
                className="prompt-btn-edit"
                onClick={() => setViewingPrompt(null)}
                style={{ backgroundColor: '#4b5563', borderColor: '#4b5563', padding: '0.5rem 1.25rem' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= EDIT MODAL ================= */}
      {editingPrompt && (
        <div className="prompt-modal-overlay" onClick={() => setEditingPrompt(null)}>
          <form onSubmit={handleSave} className="prompt-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="prompt-modal-header">
              <h5 className="prompt-modal-title">Edit: {PROMPT_DISPLAY_NAMES[editingPrompt.prompt_type] || editingPrompt.prompt_type}</h5>
              <button type="button" className="btn-close-white-custom" onClick={() => setEditingPrompt(null)}>
                ×
              </button>
            </div>

            <div className="prompt-modal-body">
              {/* Specific Informational Banner */}
              <div className="alert alert-info border-start border-4 border-info mb-4" style={{ backgroundColor: '#0f172a', borderColor: '#3b82f6' }}>
                <p className="mb-0 text-white" style={{ fontSize: '0.9rem' }}>
                  ℹ️ <strong>Only the static prompt instructions are editable.</strong> Dynamic inputs and template variables are managed by the
                  backend and are intentionally hidden to prevent accidental modification.
                </p>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold text-muted" style={{ fontSize: '0.85rem' }}>
                  Description
                </label>
                <p className="text-light-gray pt-0 mb-3" style={{ fontSize: '0.9rem', color: '#cbd5e1' }}>
                  {PROMPT_DESCRIPTIONS[editingPrompt.prompt_type] || 'Standard system prompt configuration.'}
                </p>
              </div>

              {/* Stacked sections of the prompt */}
              <div className="prompt-sections-stack">
                {editingPrompt.sections.map((section) => {
                  const isExpanded = expandedSections.has(section.id);
                  const titleDisplay = section.title === 'Header' ? 'PROMPT HEADER' : section.title.replace('## ', '');

                  return (
                    <div key={section.id} className="prompt-section-card">
                      <div className="prompt-section-header" onClick={() => toggleSection(section.id)}>
                        <div className="d-flex align-items-center gap-3">
                          <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                            {isExpanded ? '▼' : '▶'}
                          </span>
                          <span className="prompt-section-title">{titleDisplay}</span>
                        </div>
                        <div>
                          {section.isEditable ? (
                            <span className="prompt-badge-editable">Editable Section</span>
                          ) : (
                            <span className="prompt-badge-readonly">Read-Only</span>
                          )}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="prompt-section-body">
                          {section.isEditable ? (
                            <textarea
                              className="prompt-textarea-edit"
                              value={section.content}
                              onChange={(e) => handleSectionContentChange(section.id, e.target.value)}
                              rows={Math.min(25, Math.max(8, section.content.split('\n').length))}
                              required
                            />
                          ) : (
                            <div className="prompt-content-view" style={{ maxHeight: '250px' }}>
                              {section.content}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="prompt-modal-footer">
              <button
                type="button"
                className="prompt-btn-view"
                onClick={() => setEditingPrompt(null)}
                disabled={saving}
                style={{ padding: '0.5rem 1.25rem' }}
              >
                Cancel
              </button>
              <button type="submit" className="prompt-btn-edit" disabled={saving} style={{ padding: '0.5rem 1.25rem' }}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PromptManagement;
